import {Fragment} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {PercentIcon, UnlockIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize} from "@pg-mono/string-utils";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationModalButtonWithHiddenPrices} from "../../application/components/ApplicationModalButtonWithHiddenPrices";
import {LeadModalStep} from "../../application/types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {DetailViewHeader} from "../../atoms/DetailViewHeader";
import {LinkWithLabel} from "../../atoms/LinkWithLabel";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {AdditionalFormFieldType} from "../../lead/types/AdditionalFormFieldType";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {Country} from "../../region/types/Country";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {PropertyDetailHeaderHiddenPriceVariantPrice} from "./PropertyDetailHeaderHiddenPriceVariantPrice";

interface IProps {
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
    variant: "visible" | "blur";
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyDetailHeaderHiddenPriceVariant = (props: IProps) => {
    const {property} = props;

    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const isPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;
    const isAbroad = property.offer.region.country && property.offer.region.country !== Country.POLAND;

    const offerLink = useOfferLink(property.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: property.offer.vendor.slug, vendorId: property.offer.vendor.id});

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const gtmDataVisible = {
        clickText: InquiryButtonTypeName.NEGOTIATE,
        source: GTMEventApplicationSourceSection.HEADER_NEGOTIATE,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString()
    };

    const gtmDataHidden = {
        clickText: "Odkryj cenę",
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString(),
        gtmSourceSection: GTMEventApplicationSourceSection.HEADER_LEAD
    };

    const titleText = `${isApartment ? "Apartament" : getOfferTypeNameCapital(property.offer.type)} na sprzedaż ${property.rooms} ${roomPlural(
        property.rooms
    )} o powierzchni ${areaFormat(property.area, {precision: 2})} numer ${property.number}`;

    const variant = shouldShowPrice ? "visible" : props.variant;

    return (
        <div className={wrapperStyle}>
            <DetailViewHeader
                mainContentClassName={mainContentStyle}
                mainHeadlineClassName={mainHeadlineTextStyle}
                address={property.offer.address}
                badges={
                    <Fragment>
                        {property.is_promo && (
                            <Badge variant="label_info" icon={PercentIcon}>
                                Promocja
                            </Badge>
                        )}

                        {property.is_reserved && (
                            <Badge variant="label_warning" icon={UnlockIcon}>
                                Rezerwacja
                            </Badge>
                        )}

                        {(!property.for_sale || !isPublished) && <Badge variant="label_secondary">Oferta archiwalna</Badge>}
                    </Fragment>
                }
                cta={
                    <div className={ctaContainerStyle}>
                        <div className={priceContainerStyle}>
                            <PropertyDetailHeaderHiddenPriceVariantPrice property={property} variant={variant} />
                        </div>
                        {variant === "blur" ? (
                            <ApplicationModalButtonWithHiddenPrices
                                source={shouldShowPrice ? ApplicationSource.Property : ApplicationSource.PriceInquiry}
                                sourceSection={ApplicationSourceSection.MODAL}
                                shouldShowPrice={shouldShowPrice}
                                gtmData={gtmDataHidden}
                                vendorId={property.offer.vendor.id}
                                offerId={property.offer.id}
                                propertyId={property.id}
                                currency={property.offer.currency}
                                forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                render={({handleClick}) => (
                                    <Button className={buttonStyle} size="medium" variant="filled_primary" onClick={handleClick}>
                                        Odkryj cenę
                                    </Button>
                                )}
                            />
                        ) : (
                            <ApplicationModalButton
                                source={ApplicationSource.NegotiatePrice}
                                sourceSection={ApplicationSourceSection.MODAL}
                                useAdditionalFormField={AdditionalFormFieldType.NEGOTIATION}
                                initialStep={!isAbroad ? LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION : undefined}
                                vendorId={property.offer.vendor.id}
                                offerId={property.offer.id}
                                propertyId={property.id}
                                currency={property.offer.currency}
                                forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                render={({openModal}) => (
                                    <Button className={buttonStyle} size="medium" variant="filled_primary" onClick={() => openModal(gtmDataVisible)}>
                                        Negocjuj cenę
                                    </Button>
                                )}
                            />
                        )}
                    </div>
                }
                ctaClassName={ctaSectionStyle}
                externalLinks={
                    <h2 className={css({m: 0})}>
                        <LinkWithLabel href={offerLink} label="Inwestycja:" linkText={property.offer.name} />
                        {property.offer.investor ? (
                            <div className={investorWrapperStyle}>
                                <Text variant="info_txt_1" className={investorLabelStyle}>
                                    Inwestor:
                                </Text>
                                <Text variant="info_txt_1" className={investorNameStyle}>
                                    {property.offer.investor}
                                </Text>
                            </div>
                        ) : (
                            <LinkWithLabel href={vendorLink} label="Deweloper:" linkText={property.offer.vendor.name} />
                        )}
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={titleText}
            />
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    position: "relative",
    flexWrap: "wrap",
    marginBottom: 2,

    md: {
        marginBottom: 0
    }
});

const mainContentStyle = css({
    flexDirection: "column",
    md: {
        width: "100%",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }
});

const mainHeadlineTextStyle = css({
    md: {
        alignSelf: "center"
    }
});

const investorWrapperStyle = css({
    lineHeight: "1.2rem",
    minHeight: "1.6rem"
});

const investorLabelStyle = css({
    marginRight: "0.2rem",
    color: "var(--colors-gray-700)",
    display: "inline"
});

const investorNameStyle = css({
    lineHeight: 0,
    display: "inline"
});

const ctaSectionStyle = css({
    display: "flex",
    gap: 2,
    flexDirection: "column",
    width: "100%",
    flex: "0 0 auto",
    md: {
        width: "auto",
        flexDirection: "row",
        alignItems: "center",
        gap: 3
    }
});

const ctaContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    md: {
        flexDirection: "row",
        gap: 3
    }
});

const priceContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const buttonStyle = css({
    px: 4
});
import {Fragment} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {PercentIcon, UnlockIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize} from "@pg-mono/string-utils";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationModalButtonWithHiddenPrices} from "../../application/components/ApplicationModalButtonWithHiddenPrices";
import {LeadModalStep} from "../../application/types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {DetailViewHeader} from "../../atoms/DetailViewHeader";
import {LinkWithLabel} from "../../atoms/LinkWithLabel";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {AdditionalFormFieldType} from "../../lead/types/AdditionalFormFieldType";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {Country} from "../../region/types/Country";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {PropertyDetailHeaderHiddenPriceVariantPrice} from "./PropertyDetailHeaderHiddenPriceVariantPrice";

interface IProps {
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
    variant: "visible" | "blur";
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyDetailHeaderHiddenPriceVariant = (props: IProps) => {
    const {property} = props;

    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const isPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;
    const isAbroad = property.offer.region.country && property.offer.region.country !== Country.POLAND;

    const offerLink = useOfferLink(property.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: property.offer.vendor.slug, vendorId: property.offer.vendor.id});

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const gtmDataVisible = {
        clickText: InquiryButtonTypeName.NEGOTIATE,
        source: GTMEventApplicationSourceSection.HEADER_NEGOTIATE,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString()
    };

    const gtmDataHidden = {
        clickText: "Odkryj cenę",
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString(),
        gtmSourceSection: GTMEventApplicationSourceSection.HEADER_LEAD
    };

    const titleText = `${isApartment ? "Apartament" : getOfferTypeNameCapital(property.offer.type)} na sprzedaż ${property.rooms} ${roomPlural(
        property.rooms
    )} o powierzchni ${areaFormat(property.area, {precision: 2})} numer ${property.number}`;

    const variant = shouldShowPrice ? "visible" : props.variant;

    return (
        <div className={wrapperStyle}>
            <DetailViewHeader
                mainContentClassName={mainContentStyle}
                mainHeadlineClassName={mainHeadlineTextStyle}
                address={property.offer.address}
                badges={
                    <Fragment>
                        {property.is_promo && (
                            <Badge variant="label_info" icon={PercentIcon}>
                                Promocja
                            </Badge>
                        )}

                        {property.is_reserved && (
                            <Badge variant="label_warning" icon={UnlockIcon}>
                                Rezerwacja
                            </Badge>
                        )}

                        {(!property.for_sale || !isPublished) && <Badge variant="label_secondary">Oferta archiwalna</Badge>}
                    </Fragment>
                }
                cta={
                    <div className={ctaContainerStyle}>
                        <div className={priceContainerStyle}>
                            <PropertyDetailHeaderHiddenPriceVariantPrice property={property} variant={variant} />
                        </div>
                        {variant === "blur" ? (
                            <ApplicationModalButtonWithHiddenPrices
                                source={shouldShowPrice ? ApplicationSource.Property : ApplicationSource.PriceInquiry}
                                sourceSection={ApplicationSourceSection.MODAL}
                                shouldShowPrice={shouldShowPrice}
                                gtmData={gtmDataHidden}
                                vendorId={property.offer.vendor.id}
                                offerId={property.offer.id}
                                propertyId={property.id}
                                currency={property.offer.currency}
                                forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                render={({handleClick}) => (
                                    <Button className={buttonStyle} size="medium" variant="filled_primary" onClick={handleClick}>
                                        Odkryj cenę
                                    </Button>
                                )}
                            />
                        ) : (
                            <ApplicationModalButton
                                source={ApplicationSource.NegotiatePrice}
                                sourceSection={ApplicationSourceSection.MODAL}
                                useAdditionalFormField={AdditionalFormFieldType.NEGOTIATION}
                                initialStep={!isAbroad ? LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION : undefined}
                                vendorId={property.offer.vendor.id}
                                offerId={property.offer.id}
                                propertyId={property.id}
                                currency={property.offer.currency}
                                forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                render={({openModal}) => (
                                    <Button className={buttonStyle} size="medium" variant="filled_primary" onClick={() => openModal(gtmDataVisible)}>
                                        Negocjuj cenę
                                    </Button>
                                )}
                            />
                        )}
                    </div>
                }
                ctaClassName={ctaSectionStyle}
                externalLinks={
                    <h2 className={css({m: 0})}>
                        <LinkWithLabel href={offerLink} label="Inwestycja:" linkText={property.offer.name} />
                        {property.offer.investor ? (
                            <div className={investorWrapperStyle}>
                                <Text variant="info_txt_1" className={investorLabelStyle}>
                                    Inwestor:
                                </Text>
                                <Text variant="info_txt_1" className={investorNameStyle}>
                                    {property.offer.investor}
                                </Text>
                            </div>
                        ) : (
                            <LinkWithLabel href={vendorLink} label="Deweloper:" linkText={property.offer.vendor.name} />
                        )}
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={titleText}
            />
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    position: "relative",
    flexWrap: "wrap",
    marginBottom: 2,

    md: {
        marginBottom: 0
    }
});

const mainContentStyle = css({
    flexDirection: "column",
    md: {
        width: "100%",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }
});

const mainHeadlineTextStyle = css({
    md: {
        alignSelf: "center"
    }
});

const investorWrapperStyle = css({
    lineHeight: "1.2rem",
    minHeight: "1.6rem"
});

const investorLabelStyle = css({
    marginRight: "0.2rem",
    color: "var(--colors-gray-700)",
    display: "inline"
});

const investorNameStyle = css({
    lineHeight: 0,
    display: "inline"
});

const ctaSectionStyle = css({
    display: "flex",
    gap: 2,
    flexDirection: "column",
    width: "100%",
    flex: "0 0 auto",
    md: {
        width: "auto",
        flexDirection: "row",
        alignItems: "center",
        gap: 3
    }
});

const ctaContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    md: {
        flexDirection: "row",
        gap: 3
    }
});

const priceContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const buttonStyle = css({
    px: 4
});
